import { useCallback } from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { authDisabled } from '../lib/env.js';
import { loginRequest } from '../lib/msal.js';

/** Mirrors the backend's AdminRole union — Entra app roles on the ID token. */
export type AdminRole = 'Admin' | 'Sales' | 'Viewer';

export interface AdminUser {
  name: string;
  username: string;
  roles: AdminRole[];
}

const LOCAL_USER: AdminUser = {
  name: 'Local Developer',
  username: 'local@dev',
  roles: ['Admin'],
};

export function useAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const account = accounts[0];

  const user: AdminUser | undefined = authDisabled
    ? LOCAL_USER
    : account
      ? {
          name: account.name ?? account.username,
          username: account.username,
          roles: ((account.idTokenClaims?.roles as AdminRole[] | undefined) ?? []).filter(
            (role): role is AdminRole => ['Admin', 'Sales', 'Viewer'].includes(role),
          ),
        }
      : undefined;

  const login = useCallback(async () => {
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(async () => {
    await instance.logoutRedirect({ account });
  }, [instance, account]);

  /** ID token for API calls; the backend verifies it against Microsoft's JWKS. */
  const getToken = useCallback(async (): Promise<string> => {
    if (authDisabled) {
      return 'local-dev-token';
    }
    if (!account) {
      throw new Error('Not signed in');
    }
    try {
      const result = await instance.acquireTokenSilent({ ...loginRequest, account });
      return result.idToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ ...loginRequest, account });
        throw new Error('Redirecting to sign in', { cause: error });
      }
      throw error;
    }
  }, [instance, account]);

  const hasRole = useCallback(
    (...allowed: AdminRole[]) => (user?.roles ?? []).some((role) => allowed.includes(role)),
    [user],
  );

  return {
    user,
    isAuthenticated: authDisabled || Boolean(account),
    isLoading: !authDisabled && inProgress !== 'none',
    login,
    logout,
    getToken,
    hasRole,
  };
}
